import { MutableRefObject, useCallback } from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { Node, XYPosition } from 'reactflow';
import useCiaData from '@components/EntityDrawers/components/CiaInputsGroup/hooks/useCiaData';
import { OperatingModelForPartners } from '@components/EntityDrawers/constants/common';
import { commonTransformers } from '@components/EntityDrawers/constants/fields';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { resolveCreatedNode } from '@components/EntityDrawers/createdNode.helpers';
import { AssetKeys } from '@components/EntityDrawers/drawers/Assessment/keys';
import useEntityVisibilityCheck from '@components/EntityDrawers/hooks/useEntityVisibilityCheck';
import useResetSelectionAfterSave from '@components/EntityDrawers/hooks/useResetSelectionAfterSave';
import useSourceNode from '@components/EntityDrawers/hooks/useSourceNode';
import useTransformContext from '@components/EntityDrawers/hooks/useTransformContext';
import { emitNodeSaved } from '@components/EntityDrawers/nodeSaveObservable';
import useStore from '@components/MainStage/store';
import { AssetsSubLayerTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  subLayerParent,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { DateFormats } from '@constants/dates';
import { EditNodeDrawerMap } from '@constants/entities/drawers';
import { ISelectOption } from '@constants/entities/ui';
import { STANDARD_REF_UPDATED_IN_INSIGHTS } from '@constants/localStorage';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import { useAppDispatch } from '@hooks/store';
import useAssets from '@hooks/useAssets';
import useCurrentAsset from '@hooks/useCurrentAsset';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { nodesApi, useCreateProjectNodeMutation } from '@store/services/nodes';
import {
  AssessmentMetaData,
  ICreateNodeRequest,
  IUpdateNodeRequest,
  NodeDTO,
} from '@store/services/nodes/types';
import { projectsApi } from '@store/services/projects';
import { QueryTags } from '@store/services/tags';
import { add, prepareCia, stripRequirementAssurance } from '@utils/drawers';
import {
  formatNodeDTOToCanvasNode,
  getNodeTypeTypeByForSubLayer,
  parseErrorResponse,
} from '@utils/helpers';
import { Tabs } from '@views/Insights/types';

export type NodeSaveOptions<T extends FieldValues, M> = {
  editMode?: boolean;
  form: UseFormReturn<T>;
  transformMetaDataToRequestBody: (
    values: T,
    context: { subLayer: SubLayerTypes; asset: NodeDTO<AssessmentMetaData> },
  ) => Record<keyof M, any>;
  onUpdate: (
    body: IUpdateNodeRequest,
    options?: {
      closeDrawer?: boolean;
      onCompleted?: (isHidden: boolean) => void;
      onCancelled?: () => void;
    },
  ) => Promise<void>;
  drawerEditingNodeIdRef: MutableRefObject<string | null>;
};

const useNodeSave = <T extends FieldValues, M>({
  form: { getValues, setError, setValue },
  editMode,
  transformMetaDataToRequestBody,
  onUpdate,
  drawerEditingNodeIdRef,
}: NodeSaveOptions<T, M>) => {
  const projectId = useProjectId(true);
  const setActiveNode = useStore((s) => s.setActiveNode);
  const resetSelectionAfterSave = useResetSelectionAfterSave();

  const {
    hideDrawer,
    setDrawer,
    toolbox: { mode },
    setHasChanges,
    isInsightsMode,
    insightsTab,
  } = useProject();

  const insightRestoresSelection =
    isInsightsMode &&
    (insightsTab === Tabs.Maturity || insightsTab === Tabs.FocusOn);

  const { showToast } = useToast();
  const sourceNodeFromHook = useSourceNode();
  const { context } = useTransformContext();
  const dispatch = useAppDispatch();
  const { isNewAssetMode, resetNewAssetMode, selectAsset, selectedAssetId } =
    useAssets();
  const { dto: asset } = useCurrentAsset();
  const { isCiaActivated } = useCiaData();

  const { checkIsEntityBecameHidden } = useEntityVisibilityCheck();
  const { saveResponseToRedux } = useSaveEntitiesResponse();

  const [createNode, { isLoading: isCreating }] =
    useCreateProjectNodeMutation();

  const resolveCanvasNodeForSave = useCallback((): Node | undefined => {
    const state = useStore.getState();
    const byRefId = drawerEditingNodeIdRef.current;
    if (byRefId) {
      const byId = state.getNodeById(byRefId);
      if (byId?.parentNode) return byId;
    }
    const selected = state.getActiveNode();
    if (selected?.parentNode) return selected;
    return undefined;
  }, [drawerEditingNodeIdRef]);

  const prepareDraftBody = (
    id: string,
    position: XYPosition,
    canvasNode: Pick<Node, 'parentNode'>,
  ): IUpdateNodeRequest => {
    const values = getValues();

    const isPartnersEnabled = OperatingModelForPartners.includes(
      values[CommonKeys.OperationalModel],
    );

    const parentNodeLayer = canvasNode.parentNode ?? '';

    const metaData =
      transformMetaDataToRequestBody(getValues(), {
        subLayer: parentNodeLayer as SubLayerTypes,
        asset,
      }) ?? {};

    const nodeBody: IUpdateNodeRequest = {
      nodeId: id,
      assetId: selectedAssetId || undefined,

      mode,

      ...add(isCiaActivated, {
        is_reported: values[CommonKeys.IsReported] ?? true,
      }),

      name: values[CommonKeys.Name] || '',
      requirement_owner:
        values[CommonKeys.Owners]?.map(
          ({ value }: { value: string }) => value,
        ) ?? [],

      domains_id:
        values[CommonKeys.Domains]?.map(({ value }: ISelectOption) => value) ??
        [],

      ...(values[CommonKeys.ValidationStatus]
        ? {
            valid_status: values[CommonKeys.ValidationStatus],
          }
        : {}),

      reviewed_at: values[CommonKeys.DateReviewed]?.isValid()
        ? values[CommonKeys.DateReviewed].format(DateFormats[2])
        : null,

      meta_data: isCiaActivated
        ? metaData
        : stripRequirementAssurance(metaData),

      y: position.y,
      x: position.x,

      qnous_ref:
        values[CommonKeys.QnousRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      nist:
        values[CommonKeys.NistRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      iso:
        values[CommonKeys.IsoRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      ism:
        values[CommonKeys.IsmRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      cis:
        values[CommonKeys.CisRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      description: values[CommonKeys.Description] || null,

      ...add(isPartnersEnabled, {
        partners: (values[CommonKeys.Partners] ?? []).map(
          ({ value }: ISelectOption) => value,
        ),
      }),

      ...(selectedAssetId && { asset_id: selectedAssetId }),

      ...(values[AssetKeys.BusinessAttributes]
        ? {
            businessAttributes: (
              (values[AssetKeys.BusinessAttributes] ?? []) as ISelectOption[]
            ).map(({ value, label }: ISelectOption) => ({
              id: value as string,
              name: label,
            })),
          }
        : {}),

      ...add(isCiaActivated && CiaSubLayers.includes(parentNodeLayer as any), {
        cia: prepareCia({
          assurance_level: values[CommonKeys.Assurance],
          coverage: values[CommonKeys.Coverage],
          applicability: values[CommonKeys.Applicability],
        }),
      }),
    };

    return nodeBody;
  };

  const handleCreateNode = async (
    nodeBody: IUpdateNodeRequest,
    subLayerId: SubLayerTypes,
  ): Promise<{ createdDto?: NodeDTO; isHidden: boolean }> => {
    const type = getNodeTypeTypeByForSubLayer(subLayerId);
    const isAssetSubLayer = AssetsSubLayerTypes.includes(subLayerId);

    const { nodeId, ...draftBody } = nodeBody;

    const createNodeBody: ICreateNodeRequest = {
      ...draftBody,
      projectId,
      type,
      ...(isAssetSubLayer && selectedAssetId
        ? { parent_asset: selectedAssetId }
        : {}),
    };

    const knownNodeIds = new Set(useStore.getState().nodes.map(({ id }) => id));

    const response = await createNode(createNodeBody).unwrap();

    const createdNode = resolveCreatedNode(
      response.nodes,
      knownNodeIds,
      nodeBody.name,
    );

    if (isNewAssetMode) {
      dispatch(nodesApi.util.invalidateTags([QueryTags.AssetsList]));
      selectAsset(createdNode?.id ?? '');
      resetNewAssetMode();
    }

    const isHidden = !!createdNode && checkIsEntityBecameHidden(createdNode);

    saveResponseToRedux(response);
    return { createdDto: createdNode, isHidden };
  };

  const handleSave = async (options?: { fromShortcut?: boolean }) => {
    const fromShortcut = Boolean(options?.fromShortcut);

    try {
      const resolvedNode =
        resolveCanvasNodeForSave() ?? (sourceNodeFromHook as Node | undefined);

      if (!resolvedNode?.parentNode) {
        throw new Error('Active node not found or has no parent node');
      }

      const { id, position, parentNode } = resolvedNode;

      const nodeBody = prepareDraftBody(id, position, resolvedNode);
      const displayName = nodeBody.name || 'entity';

      if (editMode) {
        await onUpdate(nodeBody, {
          closeDrawer: !fromShortcut,
          onCompleted: (isHidden) => {
            setActiveNode(id);
            setHasChanges(false);

            emitNodeSaved(id, 'update');
            const skipProjectNodesInvalidation = isInsightsMode;
            dispatch(
              projectsApi.util.invalidateTags(
                skipProjectNodesInvalidation
                  ? [QueryTags.Project]
                  : [QueryTags.Project, QueryTags.ProjectNodes],
              ),
            );
            const standardRefUpdated =
              isInsightsMode &&
              insightsTab === Tabs.Completeness &&
              localStorage.getItem(STANDARD_REF_UPDATED_IN_INSIGHTS) === 'true';

            if (!standardRefUpdated && !isHidden) {
              showToast(
                fromShortcut
                  ? 'Changes saved'
                  : `The ${displayName} has been successfully updated.`,
                'success',
              );
            }

            if (!insightRestoresSelection) {
              resetSelectionAfterSave(fromShortcut);
            }
          },
          // A blocked domain change is rejected before the server writes anything,
          // so the entity still holds the scope its DTO reports. Re-derive it with
          // the transformer that filled the form when the drawer opened.
          onCancelled: () => {
            const previousDomains = commonTransformers[CommonKeys.Domains](
              resolvedNode.data?.dto,
              context,
            );

            setValue(
              CommonKeys.Domains as Path<T>,
              previousDomains as PathValue<T, Path<T>>,
              { shouldDirty: true },
            );
          },
        });

        return;
      }

      const { createdDto, isHidden } = await handleCreateNode(
        nodeBody,
        parentNode as SubLayerTypes,
      );

      dispatch(
        projectsApi.util.invalidateTags([
          QueryTags.Project,
          QueryTags.ProjectNodes,
        ]),
      );
      setHasChanges(false);

      if (createdDto?.id) {
        setActiveNode(createdDto.id);
      }

      if (fromShortcut && createdDto?.id) {
        const createdCanvasNode = formatNodeDTOToCanvasNode(createdDto);
        const layerId =
          subLayerParent[createdCanvasNode.parentNode as SubLayerTypes];
        const editDrawerType = layerId && EditNodeDrawerMap[layerId];
        if (editDrawerType) {
          setDrawer({
            type: editDrawerType,
            payload: { node: createdCanvasNode, autoFocusName: true },
          });
        } else {
          hideDrawer();
        }
      } else {
        hideDrawer();
      }

      emitNodeSaved(createdDto?.id ?? id, 'create');

      if (!isHidden) {
        showToast(
          fromShortcut
            ? 'Changes saved'
            : `The ${displayName} has been successfully created.`,
          'success',
        );
      }

      if (!insightRestoresSelection) {
        resetSelectionAfterSave(fromShortcut);
      }
    } catch (error: any) {
      const message = error.message || parseErrorResponse(error);

      if (error.data?.field && error.data.field in getValues()) {
        return setError(error.data.field, { message });
      }

      showToast(message, 'error');
    }
  };

  return {
    handleSave,
    isLoading: isCreating,
  };
};

export default useNodeSave;
